import React, { useState, useCallback, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';

export interface FileAttachmentItem {
  id: number;
  fileName?: string | null;
  filePath?: string | null;
  fileType?: string | null;
  fileSize?: number | null;
  uploadedAt?: string | null;
}

interface EntityImageGalleryProps {
  files: FileAttachmentItem[];
  /** URL or path for each image (used as img src when fetchImage is not provided) */
  getImageUrl: (fileId: number) => string;
  /** When provided, images are loaded via this (e.g. with auth); should return a blob URL or data URL */
  fetchImage?: (fileId: number) => Promise<string>;
  /** When provided, each image shows a delete button and this is called on delete (e.g. in edit form) */
  onDeleteFile?: (fileId: number) => Promise<void>;
  /** When provided, one image can be marked as primary (e.g. "Use in offers"); show star and call this on set */
  primaryFileId?: number | null;
  onSetPrimaryFile?: (fileId: number) => Promise<void>;
  /** When provided, each image shows a resize button that calls this (e.g. open resize modal) */
  onResizeFile?: (fileId: number) => void;
}

function isImageFile(file: FileAttachmentItem): boolean {
  const t = file.fileType?.toLowerCase() || '';
  return t.startsWith('image/');
}

function AuthImage({
  fileId,
  alt,
  className,
  getImageUrl,
  fetchImage,
}: {
  fileId: number;
  alt: string;
  className: string;
  getImageUrl: (fileId: number) => string;
  fetchImage: (fileId: number) => Promise<string>;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const fetchRef = React.useRef(fetchImage);
  fetchRef.current = fetchImage;
  useEffect(() => {
    let blobUrl: string | null = null;
    fetchRef.current(fileId)
      .then((url) => {
        blobUrl = url;
        setSrc(url);
      })
      .catch(() => setError(true));
    return () => {
      if (blobUrl && blobUrl.startsWith('blob:')) URL.revokeObjectURL(blobUrl);
    };
  }, [fileId]);
  if (error || !src) {
    return (
      <div className={cn(className, 'bg-gray-100 flex items-center justify-center')}>
        <span className="text-xs text-gray-500">{error ? 'Failed to load' : '…'}</span>
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} />;
}

export const EntityImageGallery: React.FC<EntityImageGalleryProps> = ({ files, getImageUrl, fetchImage, onDeleteFile, primaryFileId, onSetPrimaryFile, onResizeFile }) => {
  const { colors } = useTheme();
  const images = files.filter(isImageFile);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [settingPrimaryId, setSettingPrimaryId] = useState<number | null>(null);

  const goPrev = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex(lightboxIndex > 0 ? lightboxIndex - 1 : images.length - 1);
  }, [lightboxIndex, images.length]);

  const goNext = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex(lightboxIndex < images.length - 1 ? lightboxIndex + 1 : 0);
  }, [lightboxIndex, images.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, goPrev, goNext]);

  const handleDelete = useCallback(
    async (e: React.MouseEvent, fileId: number) => {
      e.stopPropagation();
      if (!onDeleteFile || deletingId !== null) return;
      setDeletingId(fileId);
      try {
        await onDeleteFile(fileId);
      } finally {
        setDeletingId(null);
      }
    },
    [onDeleteFile, deletingId],
  );

  const handleSetPrimary = useCallback(
    async (e: React.MouseEvent, fileId: number) => {
      e.stopPropagation();
      if (!onSetPrimaryFile || settingPrimaryId !== null) return;
      setSettingPrimaryId(fileId);
      try {
        await onSetPrimaryFile(fileId);
      } finally {
        setSettingPrimaryId(null);
      }
    },
    [onSetPrimaryFile, settingPrimaryId],
  );

  if (images.length === 0) {
    return (
      <div
        className={cn(
          'rounded-xl border-2 border-dashed py-8 px-6 text-center',
          colors.border,
          colors.surfaceHover
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>No images</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {images.map((file, index) => (
          <div
            key={file.id}
            className="relative group"
          >
            <button
              type="button"
              onClick={() => setLightboxIndex(index)}
              className={cn(
                'block w-full aspect-square rounded-lg overflow-hidden border focus:outline-none focus:ring-2 focus:ring-indigo-500',
                colors.border,
                colors.surfaceHover
              )}
            >
              {fetchImage ? (
                <AuthImage
                  fileId={file.id}
                  alt={file.fileName || 'Image'}
                  className="w-full h-full object-contain"
                  getImageUrl={getImageUrl}
                  fetchImage={fetchImage}
                />
              ) : (
                <img
                  src={getImageUrl(file.id)}
                  alt={file.fileName || 'Image'}
                  className="w-full h-full object-contain"
                />
              )}
            </button>
            {onSetPrimaryFile && images.length > 1 && (
              <button
                type="button"
                onClick={(e) => handleSetPrimary(e, file.id)}
                disabled={settingPrimaryId === file.id}
                className={cn(
                  'absolute top-2 left-2 p-1.5 rounded-md shadow-md transition-opacity',
                  primaryFileId === file.id ? 'bg-amber-500 text-white' : 'bg-white/90 dark:bg-gray-800/90 text-amber-600 dark:text-amber-400',
                  settingPrimaryId === file.id ? 'opacity-50 cursor-not-allowed' : 'opacity-90 group-hover:opacity-100'
                )}
                aria-label={primaryFileId === file.id ? 'Used in offers' : 'Use in offers'}
                title={primaryFileId === file.id ? 'Used in offers' : 'Use in offers'}
              >
                <svg className="w-4 h-4" fill={primaryFileId === file.id ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </button>
            )}
            {onResizeFile && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onResizeFile(file.id); }}
                className="absolute bottom-2 left-2 p-1.5 rounded-md shadow-md bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 opacity-90 group-hover:opacity-100"
                aria-label="Resize image"
                title="Resize (shrink file size)"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              </button>
            )}
            {onDeleteFile && (
              <button
                type="button"
                onClick={(e) => handleDelete(e, file.id)}
                disabled={deletingId === file.id}
                className={cn(
                  'absolute top-2 right-2 p-1.5 rounded-md shadow-md transition-opacity',
                  'bg-red-500 hover:bg-red-600 text-white',
                  deletingId === file.id ? 'opacity-50 cursor-not-allowed' : 'opacity-90 group-hover:opacity-100'
                )}
                aria-label="Delete image"
                title="Delete"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>

      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Image lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
            className="absolute top-4 right-4 p-2 rounded-full text-white hover:bg-white/20"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full text-white hover:bg-white/20"
                aria-label="Previous"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full text-white hover:bg-white/20"
                aria-label="Next"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
          {fetchImage ? (
            <AuthImage
              fileId={images[lightboxIndex].id}
              alt={images[lightboxIndex].fileName || 'Image'}
              className="max-w-[90vw] max-h-[90vh] object-contain"
              getImageUrl={getImageUrl}
              fetchImage={fetchImage}
            />
          ) : (
            <img
              src={getImageUrl(images[lightboxIndex].id)}
              alt={images[lightboxIndex].fileName || 'Image'}
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </>
  );
};
